import { useQuery } from '@tanstack/react-query'
import { programmeService } from '@/lib/api/services/programme'
import type {
  ApiResponse,
  PaginatedResponse,
  PaginationParams,
  Programme,
  ProgrammeDetail,
  ProgrammeFilterParams
} from '@/lib/api/types'

// Query keys for caching
export const programmeKeys = {
  all: ['programmes'] as const,
  lists: () => [...programmeKeys.all, 'list'] as const,
  list: (filters?: ProgrammeFilterParams) => [...programmeKeys.lists(), filters] as const,
  searches: () => [...programmeKeys.all, 'search'] as const,
  search: (searchTerm: string, params: PaginationParams) =>
    [...programmeKeys.searches(), searchTerm, params] as const,
  details: () => [...programmeKeys.all, 'detail'] as const,
  detail: (id: string) => [...programmeKeys.details(), id] as const,
  filtered: (params: PaginationParams, filters: ProgrammeFilterParams) =>
    [...programmeKeys.all, 'filtered', params, filters] as const
}

// Hook to fetch all programmes
export function useAllProgrammes() {
  return useQuery<ApiResponse<Programme[]>, Error>({
    queryKey: programmeKeys.lists(),
    queryFn: () => programmeService.getAllProgrammes()
  })
}

// Hook to filter programmes with pagination
export function useFilterProgrammes(
  params: PaginationParams,
  filters: ProgrammeFilterParams
) {
  return useQuery<ApiResponse<PaginatedResponse<Programme>>, Error>({
    queryKey: programmeKeys.filtered(params, filters),
    queryFn: () => programmeService.getProgrammes(params, filters)
  })
}

// Hook to search programmes with pagination
export function useSearchProgrammes(
  searchTerm: string,
  params: PaginationParams
) {
  return useQuery<ApiResponse<PaginatedResponse<Programme>>, Error>({
    queryKey: programmeKeys.search(searchTerm, params),
    queryFn: () => programmeService.searchProgrammes(searchTerm, params),
    enabled: searchTerm.trim().length > 0,
    staleTime: 5 * 60 * 1000 // 5 minutes
  })
}

// Hook to fetch a single programme
export function useProgramme(programmeId?: string) {
  return useQuery<ApiResponse<ProgrammeDetail>, Error>({
    queryKey: programmeKeys.detail(programmeId!),
    queryFn: () => programmeService.getProgrammeById(programmeId!),
    enabled: !!programmeId
  })
}

// Hook to fetch landing page programmes
export function useLandingPageProgrammes(
  params: PaginationParams = { page: 1, size: 10 }
) {
  return useQuery<ApiResponse<PaginatedResponse<Programme>>, Error>({
    queryKey: [...programmeKeys.all, 'landing-page', params],
    queryFn: () => programmeService.getLandingPageProgrammes(params)
  })
}

// Hook to fetch published programmes
export function usePublishedProgrammes(
  params: PaginationParams = { page: 1, size: 10 }
) {
  return useQuery<ApiResponse<PaginatedResponse<Programme>>, Error>({
    queryKey: [...programmeKeys.all, 'published', params],
    queryFn: () => programmeService.getPublishedProgrammes(params)
  })
}

/**
 * Hook to fetch programme highlights
 */
export function useProgrammeHighlights(type: string = 'HOME_PAGE_AFSA_PROGRAMME') {
  return useQuery<ApiResponse<Programme[]>, Error>({
    queryKey: [...programmeKeys.all, 'highlights', type],
    queryFn: () => programmeService.getProgrammeHighlights(type),
    staleTime: 5 * 60 * 1000,
  })
}
